import { router } from '@inertiajs/react'
import type { ColumnsType } from 'antd/es/table'

import { Pagination, Table, Tag } from '@/components/ui'

import { ActivityLogDetail } from './ActivityLogDetail'
import {
  ACTIVITY_LOG_PAGE_SIZES,
  type ActivityLog,
  type ActivityLogFilters,
  type ActivityLogPagination,
  activityLogQueryParams,
  formatOccurredAt,
  humanizeToken,
  outcomeTagColor,
} from './types'

export interface ActivityLogTableProps {
  logs: ActivityLog[]
  pagination: ActivityLogPagination
  /** Filters currently applied — replayed on every pagination request. */
  filters: ActivityLogFilters
  /** Index path of the surface, e.g. `/admin/activity_logs` or `/events/acme/activity_logs`. */
  basePath: string
  /** Admin sees which event a row belongs to; the event portal is already scoped. */
  showEventColumn?: boolean
  loading?: boolean
}

/**
 * The activity trail table, shared by the admin and event-portal viewers.
 *
 * Rows expand to `ActivityLogDetail` (diff + metadata + job context) so the list
 * stays scannable while the full record is one click away. Pagination is server side:
 * every page change re-issues the Inertia GET with the active filters attached.
 */
export function ActivityLogTable({
  logs,
  pagination,
  filters,
  basePath,
  showEventColumn = false,
  loading = false,
}: ActivityLogTableProps) {
  const handlePaginationChange = (page: number, pageSize: number) => {
    const nextPerPage = pageSize || pagination.per_page
    const nextPage = nextPerPage !== pagination.per_page ? 1 : page

    router.get(
      basePath,
      activityLogQueryParams(filters, { page: nextPage, per_page: nextPerPage }),
      {
        preserveState: true,
        preserveScroll: true,
        replace: true,
      },
    )
  }

  const columns: ColumnsType<ActivityLog> = [
    {
      title: 'When',
      dataIndex: 'occurred_at',
      key: 'occurred_at',
      width: 200,
      render: (value: string) => (
        <span className="whitespace-nowrap text-sm text-gray-900">{formatOccurredAt(value)}</span>
      ),
    },
    ...(showEventColumn
      ? [
          {
            title: 'Event',
            dataIndex: 'event_name',
            key: 'event_name',
            width: 180,
            ellipsis: true,
            render: (_value: unknown, record: ActivityLog) =>
              record.event_name || <span className="text-gray-400">System</span>,
          },
        ]
      : []),
    {
      title: 'Actor',
      key: 'actor',
      width: 220,
      render: (_value: unknown, record: ActivityLog) => {
        const name = record.actor_label || record.actor_name || record.actor_email

        return (
          <div className="flex flex-col">
            <span className="text-sm text-gray-900">{name || 'System'}</span>
            {record.actor_email && record.actor_email !== name && (
              <span className="text-xs text-gray-500">{record.actor_email}</span>
            )}
            {record.actor_type && (
              <span className="text-xs text-gray-400">{humanizeToken(record.actor_type)}</span>
            )}
          </div>
        )
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 240,
      render: (_value: unknown, record: ActivityLog) => (
        <div className="flex flex-col">
          <span className="text-sm text-gray-900">
            {record.action_label || humanizeToken(record.action)}
          </span>
          <span className="font-mono text-xs text-gray-400">{record.action}</span>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 160,
      render: (_value: unknown, record: ActivityLog) => (
        <span className="text-sm text-gray-500">
          {record.category_label || humanizeToken(record.category)}
        </span>
      ),
    },
    {
      title: 'Target',
      key: 'target',
      width: 220,
      render: (_value: unknown, record: ActivityLog) => {
        if (!record.target_label && !record.target_type) {
          return <span className="text-gray-400">—</span>
        }

        return (
          <div className="flex flex-col">
            <span className="text-sm text-gray-900">{record.target_label || '—'}</span>
            {record.target_type && (
              <span className="text-xs text-gray-400">
                {record.target_type}
                {record.target_id ? ` #${record.target_id}` : ''}
              </span>
            )}
          </div>
        )
      },
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 150,
      // No `source_label` counterpart: the serializers send the raw token because
      // humanizing it here yields exactly what the model would have computed.
      render: (value: string) => <span className="text-sm text-gray-500">{humanizeToken(value)}</span>,
    },
    {
      title: 'Outcome',
      dataIndex: 'outcome',
      key: 'outcome',
      width: 120,
      render: (value: string) => <Tag color={outcomeTagColor(value)}>{humanizeToken(value)}</Tag>,
    },
  ]

  return (
    <div>
      <Table<ActivityLog>
        columns={columns}
        dataSource={logs}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="middle"
        showSorterTooltip={false}
        className="ava-data-table"
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: (record) => <ActivityLogDetail log={record} />,
          // The detail panel owns its own padding and full width — see the rule for this
          // class in `application.css`, which drops the data-table cell padding/max-width.
          expandedRowClassName: () => 'ava-activity-log-detail-row',
          rowExpandable: () => true,
        }}
      />

      {pagination.total_count > 0 && (
        <div className="mt-4">
          <Pagination
            current={pagination.current_page}
            total={pagination.total_count}
            pageSize={pagination.per_page}
            pageSizeOptions={ACTIVITY_LOG_PAGE_SIZES}
            onChange={handlePaginationChange}
            showSizeChanger
            showQuickJumper
          />
        </div>
      )}
    </div>
  )
}

export default ActivityLogTable
